import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterModule } from '@angular/router';
import { DataTableDirective, DataTablesModule } from 'angular-datatables';
import { Api, Config } from 'datatables.net';
import { catchError, of, Subject } from 'rxjs';
import { User } from '../../models/user';
import { AuthenticationService } from '../../services/auth/authentication.service';
import { DynamicScriptLoaderService } from '../../services/dynamic-script-loader/dynamic-script-loader.service';
import { ModalService } from '../../services/modal/modal.service';
import { UserService } from '../../services/user/user.service';

@Component({
  selector: 'app-users',
  imports: [DataTablesModule, CommonModule, RouterModule],
  templateUrl: './users.component.html',
  styleUrl: './users.component.scss',
})
export class UsersComponent implements OnInit, AfterViewInit {
  users!: User[];
  dtOptions!: Config;

  fullyLoaded: boolean = false;

  @ViewChild(DataTableDirective)
  datatableElement!: DataTableDirective;
  dtTrigger: Subject<any> = new Subject<any>();

  constructor(
    private userService: UserService,
    private dynamicScriptLoader: DynamicScriptLoaderService,
    private authService: AuthenticationService,
    private modalService: ModalService,
    private vcr: ViewContainerRef,
    private router: Router,
    private destroyRef: DestroyRef,
    private changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.userService
      .getUsers()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.users = response;
          this.fullyLoaded = true;
          this.changeDetector.detectChanges();
          this.dtTrigger.next(null);
        },
        error: (err: HttpErrorResponse) => this.authService.logout(),
      });

    this.dtOptions = {
      pagingType: 'full_numbers',
      destroy: true,
      search: {
        caseInsensitive: true,
        smart: true,
      },
      ajax: (dataTablesParameters: any, callback) => {
        callback({ data: this.users });
      },
      lengthMenu: [5, 10, 20, 50],
      columns: [
        {
          title: 'Username',
          data: 'username',
        },
        {
          title: 'Email',
          data: 'email',
        },
        {
          title: 'Status',
          data: 'active',
          render: (data, type, row, meta) => {
            return data
              ? '<td><i class="mdi mdi-circle text-success"></i> Active</td>'
              : ' <td><i class="mdi mdi-circle text-danger"></i> Inactive</td>';
          },
        },
        {
          title: 'Action',
          data: null,
          orderable: false,
          render: (data, type, row, meta) => {
            return `<a href="javascript: void(0);" id="edit" class="action-icon"> <i class="mdi mdi-pencil"></i></a><a href="javascript: void(0);" id="delete" class="action-icon"> <i class="mdi mdi-delete"></i></a>`;
          },
        },
      ],
      rowCallback: (row: Node, data: any[] | object, index: number) => {
        $('td:last-child #delete', row).off('click');
        $('td:last-child #edit', row).off('click');
        $('td:last-child #delete', row).on('click', () =>
          this.deleteUser((data as User).uid)
        );
        $('td:last-child #edit', row).on('click', () =>
          this.editUser(data as User)
        );
      },
    };
  }

  ngAfterViewInit(): void {
    this.dtTrigger.next(null);
  }

  deleteUser(uid: string) {
    this.modalService
      .openYesNoDialog(this.vcr)
      .getAnswer()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .pipe(
        catchError((err) => {
          return of(false);
        })
      )
      .subscribe((answer) => {
        if (answer) {
          this.deleteUserConfirmed(uid);
        }
      });
  }

  deleteUserConfirmed(uid: string) {
    this.userService
      .deleteUser(uid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.datatableElement.dtInstance.then((api: Api) => {
            this.users = this.users.filter((user) => user.uid !== uid);
            this.dtTrigger.next(null);
          });
        },
        error: (err: HttpErrorResponse) => {
          this.modalService.showErrorDialog(this.vcr, err.error.message);
        },
      });
  }

  editUser(user: User) {
    localStorage.setItem('edit', JSON.stringify(user));
    this.router.navigateByUrl('dashboard/users/update');
  }
}
